/* eslint-disable react/jsx-no-bind */

// node modules
import React from 'react';
import PropTypes from 'prop-types';
import { SearchkitComponent, FacetAccessor } from 'searchkit';
import withStyles from 'isomorphic-style-loader/withStyles';

// app modules: components
import InfiniteScrollingPagination from 'components/InfiniteScrollingPagination';
import SearchResultsMatrix from 'components/SearchResultsMatrix';
import { SearchItemComponent } from 'components/SearchResultsItem';
import Heading from 'components/Heading';
import SearchSortOptions from 'components/SearchSortOptions';
import ShowMoreToggle from 'components/ShowMoreToggle';

// app modules: services
import history from 'services/history';
import { booksUrl } from 'services/route';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';

// assets
import CssSearchResults from './SearchResults.scss';

class SearchResults extends SearchkitComponent {
  static propTypes = {
    hits: PropTypes.arrayOf(
      PropTypes.shape({
        _type: PropTypes.string,
        _id: PropTypes.string,
        _source: PropTypes.object,
      }),
    ),
    searchResultProcessor: PropTypes.func,
    sortable: PropTypes.bool,
    linkToBookShelves: PropTypes.bool,
  };

  static defaultProps = {
    hits: [],
    searchResultProcessor: results => results,
    sortable: true,
    linkToBookShelves: true,
  };

  constructor(props) {
    super(props);

    this.bound = {
      updateBrowserHistory: this.updateBrowserHistory.bind(this),
    };
  }

  /**
   * @param  {String}  labelName
   * @return {Boolean}
   */
  isActive(labelName) {
    const { state } = this.searchkit.accessors.statefulAccessors.label;
    const value = state ? state.getValue() : null;

    return (
      value === null || (Array.isArray(value) && value.length === 0) || state.contains(labelName)
    );
  }

  /**
   * @param  {String}  labelName
   * @return {Boolean}
   */
  isSelected(labelName) {
    const { label } = this.searchkit.accessors.statefulAccessors;
    const buckets = label.getBuckets();
    const labelBucket = buckets.find(bucket => bucket.key === labelName);

    return !!(typeof labelBucket !== 'undefined' && labelBucket.selected);
  }

  /**
   * Toggles label state.
   * @param  {String} labelName
   */
  toggleLabel(labelName) {
    const { linkToBookShelves } = this.props;
    const filtersInUse = this.searchkit.accessors.accessors.filter(
      accessor =>
        accessor instanceof FacetAccessor &&
        !!accessor.state.value?.length &&
        accessor.key !== 'label',
    );

    if (
      linkToBookShelves &&
      labelName === 'book' &&
      !filtersInUse.length &&
      !this.isSelected(labelName)
    ) {
      history.push(
        booksUrl({
          category: 'popular',
          query: this.searchkit.query.index.queryString,
        }),
      );
    } else {
      this.searchkit.accessors.statefulAccessors.label.state = this.searchkit.accessors.statefulAccessors.label.state.toggle(
        labelName,
      );
      this.searchkit.performSearch();
    }
  }

  /**
   * Renders toggle button.
   * @param  {String} labelName]
   * @return {ReactElement}
   */
  renderToggleButton(labelName, showingMore, totalAmount) {
    if (totalAmount) {
      return (
        <ShowMoreToggle
          showingMore={showingMore}
          onClick={this.toggleLabel.bind(this, labelName)}
          totalAmount={totalAmount}
        />
      );
    }

    return null;
  }

  getToggleButtonAttributes(labelName) {
    const { label } = this.searchkit.accessors.statefulAccessors;
    const isSelected = label.state.contains(labelName);
    const bucket = label.getBuckets().filter(bucketItem => bucketItem.key === labelName)[0];
    const totalAmount = bucket ? bucket.doc_count : 0;
    return {
      showingMore: !!isSelected,
      totalAmount,
    };
  }

  updateBrowserHistory() {
    this.searchkit.performSearch(true, false, true);
  }

  renderCountAndViewMore(labelName, displayLimit) {
    const { showingMore, totalAmount } = this.getToggleButtonAttributes(labelName);
    const cypressTestProperties = {
      'data-cy': `search-result-count-${labelName}`,
      'data-cy-value': totalAmount,
    };
    if (totalAmount > displayLimit || showingMore) {
      return (
        <p className="searchResults__result-count" {...cypressTestProperties}>
          {this.renderToggleButton(labelName, showingMore, totalAmount)}
        </p>
      );
    } else if (totalAmount > 0) {
      return (
        <p className="searchResults__result-count" {...cypressTestProperties}>
          ({totalAmount})
        </p>
      );
    }
    return null;
  }

  renderHeading(title, labelName, displayLimit) {
    return (
      <header className="searchResults__header">
        <Heading>{title}</Heading>
        {this.renderCountAndViewMore(labelName, displayLimit)}
      </header>
    );
  }

  /**
   * Renders book results.
   * @param  {Array.<Object>} items
   * @return {ReactElement|null}
   */
  renderBooks(items) {
    const labelName = 'book';
    const isActive = this.isActive(labelName);
    const isSelected = this.isSelected(labelName);
    if (isActive && items.length > 0) {
      return (
        <SearchResultsMatrix
          variableWidth
          ItemComponent={SearchItemComponent.Book}
          itemKey="isbn"
          itemType={CONTENT_TYPES.BOOK}
          itemWidth={120}
          itemHeight={125}
          items={items}
          showAll={isSelected}
          onItemClick={this.bound.updateBrowserHistory}
          headingRenderer={this.renderHeading.bind(this, 'Books', labelName)}
        />
      );
    }

    return null;
  }

  renderAuthors(items) {
    const labelName = 'person';
    const isActive = this.isActive(labelName);
    const isSelected = this.isSelected(labelName);

    if (isActive && items.length > 0) {
      return (
        <SearchResultsMatrix
          ItemComponent={SearchItemComponent.Author}
          itemKey="id"
          itemType={CONTENT_TYPES.PERSON}
          itemWidth={120}
          items={items}
          showAll={isSelected}
          onItemClick={this.bound.updateBrowserHistory}
          headingRenderer={this.renderHeading.bind(this, 'Authors', labelName)}
        />
      );
    }

    return null;
  }

  /**
   * Renders reference results.
   * @param  {Array.<Object>} items
   * @return {ReactElement|null}
   */
  renderReference(items) {
    const labelName = 'reference';
    const isActive = this.isActive(labelName);
    const isSelected = this.isSelected(labelName);

    if (isActive && items.length > 0) {
      return (
        <SearchResultsMatrix
          ItemComponent={SearchItemComponent.Reference}
          itemKey="id"
          itemType={CONTENT_TYPES.REFERENCE}
          itemWidth={items.length === 1 ? 600 : 300}
          items={items}
          showAll={isSelected}
          onItemClick={this.bound.updateBrowserHistory}
          headingRenderer={this.renderHeading.bind(this, 'Reference', labelName)}
        />
      );
    }

    return null;
  }

  renderCollection(items) {
    const labelName = 'collection';
    const isActive = this.isActive(labelName);
    const isSelected = this.isSelected(labelName);

    if (isActive && items.length > 0) {
      return (
        <SearchResultsMatrix
          ItemComponent={SearchItemComponent.Collection}
          itemKey="id"
          itemType={CONTENT_TYPES.COLLECTION}
          itemWidth={items.length === 1 ? 600 : 300}
          items={items}
          showAll={isSelected}
          onItemClick={this.bound.updateBrowserHistory}
          headingRenderer={this.renderHeading.bind(this, 'Collections', labelName)}
        />
      );
    }

    return null;
  }

  /**
   * Renders recipe results.
   * @param  {Array.<Object>} items
   * @return {ReactElement|null}
   */
  renderRecipes(items) {
    const { sortable } = this.props;
    const labelName = 'recipe';
    const isActive = this.isActive(labelName);

    if (isActive && items.length > 0) {
      const { totalAmount } = this.getToggleButtonAttributes(labelName);
      return (
        <React.Fragment>
          <div className="searchResults__header-row">
            <header className="searchResults__header">
              <Heading>Recipes</Heading>
              <p
                className="searchResults__result-count"
                data-cy="search-result-count-recipe"
                data-cy-value={totalAmount}
              >
                ({totalAmount})
              </p>
            </header>
            {sortable ? <SearchSortOptions /> : null}
          </div>
          <SearchResultsMatrix
            ItemComponent={SearchItemComponent.Recipe}
            itemKey="id"
            itemType={CONTENT_TYPES.RECIPE}
            itemWidth={245}
            items={items}
            showAll
            onItemClick={this.bound.updateBrowserHistory}
            withAdverts={false} // Disabling until we can get context sensitive ads working
          />
        </React.Fragment>
      );
    }

    return null;
  }

  renderAuthorsAndBooks(authors, books) {
    if (authors.length > 0 && books.length > 0) {
      return (
        <div className="searchResults__tiles">
          <div className="searchResults__tile">{this.renderAuthors(authors)}</div>
          <div className="searchResults__tile">{this.renderBooks(books)}</div>
        </div>
      );
    }

    if (authors.length > 0) {
      return this.renderAuthors(authors);
    }

    return this.renderBooks(books);
  }

  renderReferenceAndCollections(reference, collection) {
    if (reference.length > 0 && collection.length > 0) {
      return (
        <div className="searchResults__tiles">
          <div className="searchResults__tile">{this.renderCollection(collection)}</div>
          <div className="searchResults__tile">{this.renderReference(reference)}</div>
        </div>
      );
    }

    if (reference.length > 0) {
      return this.renderReference(reference);
    }

    return this.renderCollection(collection);
  }

  render() {
    const { queryString } = this.searchkit.query.index;
    const { hits } = this.props;
    if (hits && hits.length && hits[0]._explanation) {
      console.info(
        hits.map(hit => ({
          w: hit._source.label,
          t: hit._source.title,
          i: hit._source.importance,
          s: hit._score,
          e: hit._explanation,
        })),
      );
    }

    const types = hits.reduce(
      (results, hit) => {
        results[hit._source.label].push(hit._source);
        return results;
      },
      {
        book: [],
        recipe: [],
        reference: [],
        person: [],
        collection: [],
      },
    );

    const results = this.props.searchResultProcessor(types, queryString);

    return (
      <InfiniteScrollingPagination typeToScroll="recipe">
        {this.renderAuthorsAndBooks(results.person, results.book)}
        {this.renderReferenceAndCollections(results.reference, results.collection)}
        {this.renderRecipes(results.recipe)}
      </InfiniteScrollingPagination>
    );
  }
}

export default withStyles(CssSearchResults)(SearchResults);
